import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { func, object, bool } from 'prop-types';
import Button from '../Button';
import OptimizedInput from '../OptimizedInput';
import OptimizedCombobox from '../OptimizedCombobox';
import './styles.scss';

const USER_TYPE_OPTIONS = [
  { value: '1', label: 'Productor' },
  { value: '2', label: 'Simulador' },
  { value: '3', label: 'Administrador' }
];

const DEFAULT_USER_TYPE = USER_TYPE_OPTIONS[0].value;
const USER_TYPE_VALUES = USER_TYPE_OPTIONS.map(option => option.value);

const normalizeUserType = (userType) => {
  const normalizedUserType = userType?.toString();

  return USER_TYPE_VALUES.includes(normalizedUserType)
    ? normalizedUserType
    : DEFAULT_USER_TYPE;
};

const getRoleFromUserType = (userType) => (
  USER_TYPE_OPTIONS.find(option => option.value === userType?.toString())?.label
  || USER_TYPE_OPTIONS[0].label
);

const UserFormModal = ({
  user = null,
  isVisible = false,
  onSave,
  onDismiss,
  loading = false
}) => {
  // Local state for form data
  const [formData, setFormData] = useState({
    username: '',
    cuit: '',
    email: '',
    firstName: '',
    lastName: '',
    userType: DEFAULT_USER_TYPE,
    rol: 'productor',
    status: 'active',
    password: '',
    confirmPassword: '',
    phoneNumber: ''
  });

  // Local state for validation errors
  const [errors, setErrors] = useState({});

  // Options for user type select
  const userTypeOptions = useMemo(() => USER_TYPE_OPTIONS, []);

  // Options for role select
  const roleOptions = useMemo(() => [
    { value: 'productor', label: 'Productor' },
    { value: 'simulador', label: 'Simulador' },
    { value: 'gestor', label: 'Gestor' },
    { value: 'administrador', label: 'Administrador' },
    { value: 'admin', label: 'Admin' }
  ], []);

  // Options for status select
  const statusOptions = useMemo(() => [
    { value: 'active', label: 'Activo' },
    { value: 'inactive', label: 'Inactivo' }
  ], []);

  // Reset form when user prop changes or modal opens
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        cuit: user.username || '',
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        userType: normalizeUserType(user.userType),
        rol: user.rol || 'productor',
        status: user.status || 'active',
        password: '',
        confirmPassword: '',
        phoneNumber: user.phoneNumber || ''
      });
    } else {
      setFormData({
        username: '',
        cuit: '',
        email: '',
        firstName: '',
        lastName: '',
        userType: DEFAULT_USER_TYPE,
        rol: 'productor',
        status: 'active',
        password: '',
        confirmPassword: '',
        phoneNumber: ''
      });
    }
    setErrors({});
  }, [user, isVisible]);

  // Clear cuit error when userType changes
  useEffect(() => {
    if (formData.userType !== '1') {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.cuit;
        return newErrors;
      });
    }
  }, [formData.userType]);

  // Validation functions
  const validateField = useCallback((name, value) => {
    const newErrors = { ...errors };

    switch (name) {

      case 'cuit':
        if (formData.userType === '1') {
          if (!value.trim()) {
            newErrors.cuit = 'El CUIT es obligatorio para productores';
          } else if (!/^\d{11}$/.test(value.replace(/[-\s]/g, ''))) {
            newErrors.cuit = 'El CUIT debe tener 11 dígitos';
          } else {
            delete newErrors.cuit;
          }
        } else {
          delete newErrors.cuit;
        }
        break;

      case 'email':
        if (!value.trim()) {
          newErrors.email = 'El correo electrónico es obligatorio';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = 'Ingrese un correo electrónico válido';
        } else if (value.length > 100) {
          newErrors.email = 'El correo electrónico no puede exceder 100 caracteres';
        } else {
          delete newErrors.email;
        }
        break;
        
      case 'firstName':
        if (!value.trim()) {
          newErrors.firstName = 'El nombre es obligatorio';
        } else if (value.length < 2) {
          newErrors.firstName = 'El nombre debe tener al menos 2 caracteres';
        } else if (value.length > 50) {
          newErrors.firstName = 'El nombre no puede exceder 50 caracteres';
        } else {
          delete newErrors.firstName;
        }
        break;
        
      case 'lastName':
        if (!value.trim()) {
          newErrors.lastName = 'El apellido es obligatorio';
        } else if (value.length < 2) {
          newErrors.lastName = 'El apellido debe tener al menos 2 caracteres';
        } else if (value.length > 50) {
          newErrors.lastName = 'El apellido no puede exceder 50 caracteres';
        } else {
          delete newErrors.lastName;
        }
        break;
        
      case 'password':
        if (!user && !value.trim()) {
          newErrors.password = 'La contraseña es obligatoria';
        } else if (value && value.length < 6) {
          newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
        } else if (value && value.length > 100) {
          newErrors.password = 'La contraseña no puede exceder 100 caracteres';
        } else {
          delete newErrors.password;
        }
        break;
        
      case 'confirmPassword':
        if (!user && !value.trim()) {
          newErrors.confirmPassword = 'La confirmación de contraseña es obligatoria';
        } else if (value && value !== formData.password) {
          newErrors.confirmPassword = 'Las contraseñas no coinciden';
        } else {
          delete newErrors.confirmPassword;
        }
        break;
        
      case 'phoneNumber':
        if (value && !/^\+?[\d\s\-()]+$/.test(value)) {
          newErrors.phoneNumber = 'Ingrese un número de teléfono válido';
        } else {
          delete newErrors.phoneNumber;
        }
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [errors, user, formData.password]);

  // Validate entire form
  const validateForm = useCallback(() => {
    let isValid = true;
    Object.keys(formData).forEach(field => {
      // Skip password validation in edit mode if empty
      if (user && (field === 'password' || field === 'confirmPassword') && !formData[field]) {
        return;
      }
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });
    return isValid;
  }, [formData, validateField, user]);

  // Handle input changes for OptimizedInput
  const handleInputChange = useCallback((event) => {
    const inputName = event.target.name || event.target.id;
    const inputValue = event.target.value;
    
    // Update local state
    setFormData(prev => ({
      ...prev,
      [inputName]: inputValue
    }));
    
    // Validate field in real-time
    validateField(inputName, inputValue);
  }, [validateField]);

  // Handle select changes for OptimizedCombobox
  const handleSelectChange = useCallback((option, syntheticEvent) => {
    const inputName = syntheticEvent.target.name;
    
    // Update local state
    setFormData(prev => ({
      ...prev,
      [inputName]: option.value
    }));
    
    // Validate field in real-time
    validateField(inputName, option.value);
  }, [validateField]);

  const handleNativeSelectChange = useCallback((event) => {
    const inputName = event.target.name;
    const inputValue = event.target.value;

    setFormData(prev => ({
      ...prev,
      [inputName]: inputValue
    }));

    validateField(inputName, inputValue);
  }, [validateField]);

  // Handle form submission
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    // Validate entire form before submission
    if (!validateForm()) {
      return;
    }
    
    // Prepare data for the API
    // For Productor (userType '1'), use cuit as username
    const userNameValue = formData.userType === '1' ? formData.cuit : formData.username;

    const apiData = {
      userName: userNameValue,
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phoneNumber: formData.phoneNumber || '',
      userType: parseInt(formData.userType),
      role: getRoleFromUserType(formData.userType),
      status: formData.status,
      emailConfirmed: formData.status === 'active'
    };
    
    // Only include password if it's provided (new user or password change)
    if (!user && formData.password) {
      apiData.password = formData.password;
    } else if (user && formData.password) {
      apiData.password = formData.password;
    }
    
    onSave(apiData);
  }, [formData, user, onSave, validateForm]);

  // Check if form is valid for submit button
  const isFormValid = useMemo(() => {
    const requiredFields = ['email', 'firstName', 'lastName'];

    // Check if all required fields have values
    const hasAllRequiredFields = requiredFields.every(field =>
      formData[field] && formData[field].trim() !== ''
    );

    // For Productor (userType '1'), cuit is required
    if (formData.userType === '1') {
      const hasCuit = formData.cuit && formData.cuit.trim() !== '';
      if (!hasCuit) return false;
    }

    // For new users, password is required
    if (!user) {
      const hasPassword = formData.password && formData.password.trim() !== '';
      const hasConfirmPassword = formData.confirmPassword && formData.confirmPassword.trim() !== '';
      if (!hasPassword || !hasConfirmPassword) return false;
    }

    // Check if there are no errors
    const hasNoErrors = Object.keys(errors).length === 0;

    return hasAllRequiredFields && hasNoErrors;
  }, [formData, errors, user]);

  if (!isVisible) {
    return null;
  }

  const isEditing = !!user;
  const modalTitle = isEditing ? 'Editar Usuario' : 'Nuevo Usuario';

  return (
    <div 
      className="modal user-form-modal fade show d-block" 
      tabIndex="-1" 
      role="dialog" 
      aria-hidden="false"
      aria-labelledby="user-form-modal-title"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 id="user-form-modal-title" className="modal-title">
              {modalTitle}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onDismiss}
              aria-label="Cerrar"
              disabled={loading}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body p-4">
                              {/* User Type and Role Row */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="userType" className="form-label">
                    Tipo de Usuario
                  </label>
                  <select
                    id="userType"
                    name="userType"
                    className="form-select"
                    value={formData.userType}
                    onChange={handleNativeSelectChange}
                    disabled={loading}
                  >
                    {userTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Username and Email Row */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <OptimizedInput
                    id="email"
                    name="email"
                    label="Correo Electrónico *"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    maxLength="100"
                    placeholder="ej: usuario@ejemplo.com"
                    className={errors.email ? 'is-invalid' : ''}
                    disabled={loading}
                  />
                  {errors.email && (
                    <div className="invalid-feedback d-block">
                      {errors.email}
                    </div>
                  )}
                </div>
                {formData.userType === '1' && (
                  <div className="col-md-6">
                    <OptimizedInput
                      id="cuit"
                      name="cuit"
                      label="CUIT *"
                      type="text"
                      value={formData.cuit}
                      onChange={handleInputChange}
                      maxLength="11"
                      placeholder="ej: 20123456789"
                      className={errors.cuit ? 'is-invalid' : ''}
                      disabled={loading}
                    />
                    {errors.cuit && (
                      <div className="invalid-feedback d-block">
                        {errors.cuit}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* First Name and Last Name Row */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <OptimizedInput
                    id="firstName"
                    name="firstName"
                    label="Nombre *"
                    type="text"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    maxLength="50"
                    placeholder="Ingrese el nombre"
                    className={errors.firstName ? 'is-invalid' : ''}
                    disabled={loading}
                  />
                  {errors.firstName && (
                    <div className="invalid-feedback d-block">
                      {errors.firstName}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <OptimizedInput
                    id="lastName"
                    name="lastName"
                    label="Apellido *"
                    type="text"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    maxLength="50"
                    placeholder="Ingrese el apellido"
                    className={errors.lastName ? 'is-invalid' : ''}
                    disabled={loading}
                  />
                  {errors.lastName && (
                    <div className="invalid-feedback d-block">
                      {errors.lastName}
                    </div>
                  )}
                </div>
              </div>


              {/* Phone Number Row */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <OptimizedInput
                    id="phoneNumber"
                    name="phoneNumber"
                    label="Teléfono"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    maxLength="20"
                    placeholder="ej: +54 11 1234-5678"
                    className={errors.phoneNumber ? 'is-invalid' : ''}
                    disabled={loading}
                  />
                  {errors.phoneNumber && (
                    <div className="invalid-feedback d-block">
                      {errors.phoneNumber}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <OptimizedCombobox
                    id="status"
                    name="status"
                    label="Estado"
                    options={statusOptions}
                    value={formData.status}
                    onChange={handleSelectChange}
                    placeholder="Seleccionar estado"
                    isSearchable={false}
                    isDisabled={loading}
                  />
                </div>
              </div>

              {/* Password Fields (Only for new users or password change) */}
              {!isEditing && (
                <div className="row mb-3">
                  <div className="col-md-6">
                    <OptimizedInput
                      id="password"
                      name="password"
                      label="Contraseña *"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      maxLength="100"
                      placeholder="Ingrese contraseña"
                      className={errors.password ? 'is-invalid' : ''}
                      disabled={loading}
                    />
                    {errors.password && (
                      <div className="invalid-feedback d-block">
                        {errors.password}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <OptimizedInput
                      id="confirmPassword"
                      name="confirmPassword"
                      label="Confirmar Contraseña *"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      maxLength="100"
                      placeholder="Confirme contraseña"
                      className={errors.confirmPassword ? 'is-invalid' : ''}
                      disabled={loading}
                    />
                    {errors.confirmPassword && (
                      <div className="invalid-feedback d-block">
                        {errors.confirmPassword}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Password Field for editing (optional) */}
              {isEditing && (
                <div className="row mb-3">
                  <div className="col-12">
                    <OptimizedInput
                      id="password"
                      name="password"
                      label="Nueva Contraseña (dejar en blanco para mantener actual)"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      maxLength="100"
                      placeholder="Ingrese nueva contraseña"
                      className={errors.password ? 'is-invalid' : ''}
                      disabled={loading}
                    />
                    {errors.password && (
                      <div className="invalid-feedback d-block">
                        {errors.password}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer border-0">
              <Button 
                type="button" 
                color="secondary" 
                fill="outline" 
                size="smaller" 
                onClick={onDismiss}
                disabled={loading}
                customStyles={{ minWidth: '120px' }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                size="smaller"
                disabled={!isFormValid || loading}
                loading={loading}
                customStyles={{ minWidth: '120px' }}
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

UserFormModal.propTypes = {
  user: object,
  isVisible: bool,
  onSave: func.isRequired,
  onDismiss: func.isRequired,
  loading: bool
};

export default React.memo(UserFormModal);
